import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function exportSalesToPDF(sales, filename = "sales.pdf") {
  // Initialize PDF in portrait (A4)
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let currentY = 10;

  // Document properties
  doc.setProperties({
    title: "Sales",
    subject: "Sales Data Export",
    creator: "Afghan Cosmos Group",
  });

  // Format currency helper
  const formatCurrency = (value, currency) => `${value || 0} ${currency || ""}`;

  // --- HEADER (identical to purchase) ---
  const addHeader = () => {
    const shopName = "Baradaran Qaderi";

    // Logo
    try {
      const logoUrl = `${process.env.REACT_APP_IMAGE_URL}images/logo.jpg`;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = logoUrl;
      doc.addImage(img, "JPEG", pageWidth - 25, 2, 10, 10);
    } catch (error) {
      console.error("Failed to add logo:", error);
    }

    // Title
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 102, 204);
    doc.text("Sales Report", pageWidth / 2, 9, { align: "center" });

    // Shop name
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 80, 80);
    doc.text(shopName, 10, 7);

    // Date
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 10, 11);

    return 18; // Header height
  };

  currentY = addHeader();

  // Helper function to get first currency value
  const firstCurrencyValue = (obj) => {
    if (obj && typeof obj === "object") {
      const firstEntry = Object.values(obj)[0];
      if (firstEntry && typeof firstEntry === "object")
        return firstEntry.source ?? "-";
      return firstEntry ?? "-";
    }
    return obj ?? "-";
  };

  // Calculate summary totals
  const summaryTotals = sales.reduce((acc, sale) => {
    const currency = sale.currency_details?.title || "";
    const totalAmount = parseFloat(firstCurrencyValue(sale.total_amount)) || 0;
    const totalPaid = parseFloat(firstCurrencyValue(sale.total_paid)) || 0;
    const totalRemained = parseFloat(firstCurrencyValue(sale.total_remained_amount)) || 0;
    
    return {
      totalAmount: acc.totalAmount + totalAmount,
      totalPaid: acc.totalPaid + totalPaid,
      totalRemained: acc.totalRemained + totalRemained,
      currency: currency || acc.currency
    };
  }, { totalAmount: 0, totalPaid: 0, totalRemained: 0, currency: "" });

  // --- TABLES ---
  sales.forEach((sale) => {
    // Page break check
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = addHeader();
    }

    const currency = sale.currency_details?.title || "";

    // Main Sales Table (blue header)
    // Main Sales Table (blue header)
    autoTable(doc, {
      head: [
        [
          "ID",
          "Customer",
          "Date",
          "Discount",
          "Total Amount",
          "Total Paid",
          "Remained",
          "Items",
        ],
      ],
      body: [
        [
          sale.id || "N/A",
          sale.customer_details?.name || "N/A",
          sale.sale_date || "N/A",
          `${sale.discount || 0}`,
          formatCurrency(sale.total_amount?.[currency], currency),
          formatCurrency(sale.total_paid?.[currency], currency),
          formatCurrency(sale.total_remained_amount?.[currency], currency),
          sale.sale_items?.length || 0,
        ],
      ],
      startY: currentY,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fontSize: 8,
        fontStyle: "bold",
        fillColor: [51, 102, 204],
        textColor: [255, 255, 255],
        halign: "center",
        cellPadding: 2,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250],
      },
      margin: { top: 1, bottom: 1, left: 3, right: 3 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) currentY = addHeader();
      },
    });

    // ✅ Match purchase structure: add spacing between tables
    currentY = doc.lastAutoTable.finalY + 0;

    // Sale Items Table (light blue header)
    if (sale.sale_items?.length) {
      autoTable(doc, {
        head: [["Product", "Qty", "Sale Price", "Total", "Unit"]],
        body: sale.sale_items.map((item) => [
          item.product_details?.name || "N/A",
          item.quantity || 0,
          formatCurrency(item.sale_price, currency),
          formatCurrency(item.quantity * item.sale_price, currency),
          item.unit_details?.title || "N/A",
        ]),
        startY: currentY,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          halign: "center",
          valign: "middle",
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
          fillColor: [220, 230, 242],
          textColor: [50, 50, 50],
          halign: "center",
          cellPadding: 2,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        margin: { top: 1, bottom: 1, left: 3, right: 3 },
      });
      currentY = doc.lastAutoTable.finalY + 2;
    }
  });

  // Add summary table
  if (currentY > pageHeight - 40) {
    doc.addPage();
    currentY = addHeader();
  }

  autoTable(doc, {
    head: [["Summary", "Total Amount", "Total Paid", "Total Remained"]],
    body: [[
      "TOTALS",
      `${summaryTotals.totalAmount.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalPaid.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalRemained.toFixed(2)} ${summaryTotals.currency}`
    ]],
    startY: currentY + 5,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: "center",
      fontStyle: "bold",
    },
    headStyles: {
      fontSize: 10,
      fontStyle: "bold",
      fillColor: [51, 102, 204],
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fillColor: [245, 245, 245],
      fontStyle: "bold",
    },
    margin: { left: 3, right: 3 },
  });

  // --- FOOTER (identical to purchase) ---
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Divider line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(0, pageHeight - 11, pageWidth - 0, pageHeight - 11);

    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("برادران قادری", pageWidth / 2, pageHeight - 5, {
      align: "center",
    });

    // Page number
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, pageHeight - 5, {
      align: "right",
    });
  }

  doc.save(filename);
}
